/**
 * Filtering and ordering of the resource catalogs (boards and databases) - the ONE rule set shared
 * by the home dashboard and the workspace page, so the same filters mean the same thing in both.
 * Filtering hides resources the server already lists for this viewer (it never reveals others);
 * sorting only orders what is shown. Pure and unit-tested.
 */
export type ResourceKind = 'board' | 'database' | 'document';
export type TypeFilter = 'all' | ResourceKind;
export type OwnerFilter = 'any' | 'mine' | 'others';
export type SpaceFilter = 'any' | 'personal' | 'team';
/** `manual`: the stored order (workspace); `opened`: by when the person opened it; `updated`: boards only. */
export type CatalogSort = 'manual' | 'opened' | 'updated' | 'name';

export interface CatalogFilters {
  type: TypeFilter;
  /** Only the person's own favorites (boards and databases). */
  favorites: boolean;
  /** Only what the person opened lately on this device (a filter - not the same as the sort). */
  recent: boolean;
  owner: OwnerFilter;
  /** The kind of workspace the resource lives in (the home dashboard only). */
  space: SpaceFilter;
  /** Only resources carrying ANY of these labels (OR inside the category), each a label ID - labels
   * belong to one workspace, so equal names in two workspaces are two labels. An older version
   * stored a bare NAME; it is still read (see `labelMatches`). Empty = any. Combined with the
   * other filters by AND. */
  labels: string[];
  /** `null` = the default order of the page showing the catalog. */
  sort: CatalogSort | null;
}

export const DEFAULT_FILTERS: CatalogFilters = {
  type: 'all',
  favorites: false,
  recent: false,
  owner: 'any',
  space: 'any',
  labels: [],
  sort: null,
};

/** What the filters look at - a neutral description of a board or a database. */
export interface Filterable {
  kind: ResourceKind;
  favorite: boolean;
  /** Opened lately by this person on this device. */
  recent: boolean;
  ownerId: string | null;
  space: 'personal' | 'team' | null;
  /** Lower-cased fields the search looks in (name first). */
  haystack: readonly string[];
  /** The labels on it (this resource's own, never inherited from a group). */
  labels?: readonly LabelRef[];
}

export interface LabelRef {
  id: string;
  name: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Does a resource carrying `have` pass the selected labels (ANY of them)? A selected ID matches
 * exactly that label; a selected bare NAME (an older stored filter) matches by name. */
export function labelMatches(selected: readonly string[], have: readonly LabelRef[]): boolean {
  return selected.some((value) =>
    have.some(
      (label) =>
        label.id === value ||
        (!UUID.test(value) && label.name.toLocaleLowerCase() === value.toLocaleLowerCase()),
    ),
  );
}

/** The `LabelRef`s of a resource's label list (absent on resources of other workspace kinds). */
export const labelRefs = (labels: readonly LabelRef[] | undefined): LabelRef[] =>
  (labels ?? []).map(({ id, name }) => ({ id, name }));

/** Filters that differ from the default (the sort is not a filter and never counts). */
export function activeFilterCount(filters: CatalogFilters, offered?: Offered): number {
  const shows = (key: keyof Offered) => offered === undefined || offered[key] === true;
  return [
    shows('type') && filters.type !== 'all',
    shows('favorites') && filters.favorites,
    shows('recent') && filters.recent,
    shows('owner') && filters.owner !== 'any',
    shows('space') && filters.space !== 'any',
    shows('label') && filters.labels.length > 0,
  ].filter(Boolean).length;
}

/** Which filters a page offers (a workspace section is already a type; the dashboard has spaces). */
export interface Offered {
  type: boolean;
  favorites: boolean;
  recent: boolean;
  owner: boolean;
  space: boolean;
  /** The label filter (omitted = not offered). */
  label?: boolean;
}

export function matchesFilters(
  item: Filterable,
  filters: CatalogFilters,
  viewerId: string | null,
  query: string,
  offered?: Offered,
): boolean {
  const shows = (key: keyof Offered) => offered === undefined || offered[key] === true;
  if (shows('type') && filters.type !== 'all' && filters.type !== item.kind) return false;
  if (shows('favorites') && filters.favorites && !item.favorite) return false;
  if (shows('recent') && filters.recent && !item.recent) return false;
  if (shows('owner') && filters.owner !== 'any') {
    const mine = viewerId !== null && item.ownerId === viewerId;
    if (filters.owner === 'mine' ? !mine : mine) return false;
  }
  if (shows('space') && filters.space !== 'any' && item.space !== filters.space) return false;
  if (shows('label') && filters.labels.length > 0) {
    if (!labelMatches(filters.labels, item.labels ?? [])) return false;
  }
  const needle = query.trim().toLocaleLowerCase();
  return needle === '' || item.haystack.some((field) => field.includes(needle));
}

const TYPES: readonly TypeFilter[] = ['all', 'board', 'database', 'document'];
const OWNERS: readonly OwnerFilter[] = ['any', 'mine', 'others'];
const SPACES: readonly SpaceFilter[] = ['any', 'personal', 'team'];
const SORTS: readonly CatalogSort[] = ['manual', 'opened', 'updated', 'name'];

const pick = <T extends string>(list: readonly T[], value: unknown, fallback: T): T =>
  list.find((item) => item === value) ?? fallback;

const MAX_LABELS = 30;

/** The labels of a stored filter: the list, or the single name an older version stored. */
function storedLabels(stored: Record<string, unknown>): string[] {
  const raw: unknown[] = Array.isArray(stored.labels)
    ? (stored.labels as unknown[])
    : stored.label === undefined
      ? []
      : [stored.label];
  const names: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const name = item.trim().slice(0, 100);
    if (name !== '' && !names.includes(name)) names.push(name);
  }
  return names.slice(0, MAX_LABELS);
}

/** A label the filter can offer: one per label ID. `workspace` tells apart equal names that
 * belong to different workspaces (shown only when the name is ambiguous). */
export interface LabelChoice {
  id: string;
  name: string;
  color: string | null;
  workspace: string | null;
  /** What the person reads: the name, plus the workspace when another choice has the same name. */
  display: string;
}

export function labelChoices(
  labels: Iterable<{ id: string; name: string; color?: string | null; workspace?: string | null }>,
  locale?: string,
): LabelChoice[] {
  const seen = new Map<string, LabelChoice>();
  for (const label of labels) {
    if (!seen.has(label.id)) {
      seen.set(label.id, {
        id: label.id,
        name: label.name,
        color: label.color ?? null,
        workspace: label.workspace ?? null,
        display: label.name,
      });
    }
  }
  const all = [...seen.values()];
  const perName = new Map<string, number>();
  for (const c of all) {
    const key = c.name.toLocaleLowerCase();
    perName.set(key, (perName.get(key) ?? 0) + 1);
  }
  for (const c of all) {
    if (c.workspace && (perName.get(c.name.toLocaleLowerCase()) ?? 0) > 1) {
      c.display = `${c.name} · ${c.workspace}`;
    }
  }
  return all.sort(
    (a, b) => a.name.localeCompare(b.name, locale) || a.display.localeCompare(b.display, locale),
  );
}

/** The stored selection as label IDs: a bare name from an older filter becomes the IDs of every
 * offered label of that name (what it meant then); a value nothing matches is kept so that it can
 * be shown as gone and removed. */
export function selectedLabelValues(
  stored: readonly string[],
  choices: readonly LabelChoice[],
): string[] {
  const out: string[] = [];
  for (const value of stored) {
    const byId = choices.filter((c) => c.id === value);
    const found =
      byId.length > 0
        ? byId
        : UUID.test(value)
          ? []
          : choices.filter((c) => c.name.toLocaleLowerCase() === value.toLocaleLowerCase());
    for (const id of found.length > 0 ? found.map((c) => c.id) : [value]) {
      if (!out.includes(id)) out.push(id);
    }
  }
  return out;
}

/** Stored preferences from an untrusted string: anything unknown falls back to the default. */
export function parseFilters(raw: string | null): CatalogFilters {
  try {
    const value: unknown = raw === null ? null : JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return DEFAULT_FILTERS;
    const stored = value as Record<string, unknown>;
    return {
      type: pick(TYPES, stored.type, 'all'),
      favorites: stored.favorites === true,
      recent: stored.recent === true,
      owner: pick(OWNERS, stored.owner, 'any'),
      space: pick(SPACES, stored.space, 'any'),
      labels: storedLabels(stored),
      sort: SORTS.find((item) => item === stored.sort) ?? null,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

/** The sort a page uses: the stored one if the page offers it, else the page's default. */
export function effectiveSort(
  filters: CatalogFilters,
  offered: readonly CatalogSort[],
  fallback: CatalogSort,
): CatalogSort {
  return filters.sort !== null && offered.includes(filters.sort) ? filters.sort : fallback;
}
